import { SentimentScore } from '@zencity/survey-types';
import { CLIENT_TOKEN } from 'customHooks/useClientNameTokenReplacement/useClientNameTokenReplacement';
import { OverallSatisfactionScoreQuestion } from 'screens/WidgetGenerator/utils/misc';

// the max length of the question text in the chart.
export const MAX_QUESTION_LENGTH = 90;

// if the percentage is less than MIN_PERCENTAGE_VALUE we will not show it in the chart
export const MIN_PERCENTAGE_VALUE = 5;

// test ids
export const SATISFACTION_BAR_TEST_ID = 'satisfaction-bar';
export const SATISFACTION_SCORE_BAR_TEST_ID = '-score-bar';
export const LEGEND_COMPONENT = 'legend-component';

export const enum BodyRowHeight {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length > maxLength) {
    return `${text.slice(0, maxLength)}...`;
  }
  return text;
};

interface DetermineScoreDifferencePercentage {
  currentScore: number;
  previousScore?: number;
}

export const determineScoreDifferencePercentage = ({
  currentScore,
  previousScore,
}: DetermineScoreDifferencePercentage): number => {
  if (!previousScore || previousScore === 0) {
    return 0;
  }

  return Math.abs(Math.round(currentScore) - Math.round(previousScore));
};

interface IsRowHighlighted {
  currentScore: SentimentScore;
  previousScore?: SentimentScore;
}

const THRESHOLD = 8;

export const isRowHighlighted = ({ currentScore, previousScore }: IsRowHighlighted): boolean => {
  if (!previousScore) {
    return false;
  }

  const positiveDifference = determineScoreDifferencePercentage({
    currentScore: currentScore.positive,
    previousScore: previousScore.positive,
  });

  const negativeDifference = determineScoreDifferencePercentage({
    currentScore: currentScore.negative,
    previousScore: previousScore.negative,
  });

  return positiveDifference > THRESHOLD || negativeDifference > THRESHOLD;
};

// Return the top TOP_VALUE_CHANGES_AMOUNT(3) ids of largest change values (positive or negative), as long as they are bigger than 5% change.
export const determineHighlightedRows = (widgetData: OverallSatisfactionScoreQuestion[]): number[] => {
  const TOP_VALUE_CHANGES_AMOUNT = 3;

  const changesWithId = widgetData.reduce((accumulator, question) => {
    if (!question.previousCycle) {
      return accumulator; // Skip if there is no previous cycle
    }

    const currentScore = question.score;
    const previousScore = question.previousCycle.score;

    const positiveDifference = determineScoreDifferencePercentage({
      currentScore: currentScore.positive,
      previousScore: previousScore.positive,
    });

    const negativeDifference = determineScoreDifferencePercentage({
      currentScore: currentScore.negative,
      previousScore: previousScore.negative,
    });

    const biggestChange = Math.max(positiveDifference, negativeDifference);

    // If the change is less than equal to MIN_PERCENTAGE_VALUE, skip
    if (biggestChange <= MIN_PERCENTAGE_VALUE) {
      return accumulator;
    }

    // Add the change to the accumulator
    accumulator.push({
      id: question.genericQuestionId,
      change: biggestChange,
    });

    return accumulator;
  }, [] as { id: number; change: number }[]);

  // Get an array of unique changes
  const uniqueChanges = [...new Set(changesWithId.map((change) => change.change))];

  // Sort it in descending order
  const sortedUniqueChanges = uniqueChanges.sort((changeA, changeB) => changeB - changeA);

  const topValueChanges = sortedUniqueChanges.slice(0, TOP_VALUE_CHANGES_AMOUNT);

  // Map top changes back to their IDs
  return changesWithId.filter((change) => topValueChanges.includes(change.change)).map((change) => change.id);
};

export const splitWidgetDataToChunks = (
  widgetData: OverallSatisfactionScoreQuestion[],
  servicesBarChartNumRowsPerTable: number,
): OverallSatisfactionScoreQuestion[][] =>
  widgetData.reduce<OverallSatisfactionScoreQuestion[][]>((accumulator, currentValue, currentIndex) => {
    const chunkIndex = Math.floor(currentIndex / servicesBarChartNumRowsPerTable);
    if (!accumulator[chunkIndex]) {
      accumulator[chunkIndex] = [];
    }
    accumulator[chunkIndex].push(currentValue);
    return accumulator;
  }, []);

export const servicesBarChartDataFormat = (
  widgetData: OverallSatisfactionScoreQuestion[],
  clientName: string,
): OverallSatisfactionScoreQuestion[] => {
  if (!widgetData) {
    return [] as OverallSatisfactionScoreQuestion[];
  }

  return widgetData.map((item) => {
    const { text, ...rest } = item;
    return {
      text: text.replaceAll(CLIENT_TOKEN, clientName),
      ...rest,
    };
  });
};

interface GenerateWidgetData {
  selectedServiceBarAspects: string[];
  widgetData: OverallSatisfactionScoreQuestion[];
  replaceClientTokenWith?: string;
  servicesBarChartNumRowsPerTable?: number;
}

export const generateWidgetData = ({
  selectedServiceBarAspects,
  widgetData,
  replaceClientTokenWith,
  servicesBarChartNumRowsPerTable,
}: GenerateWidgetData): OverallSatisfactionScoreQuestion[][] => {
  // filter the data according to the selected aspects
  const filteredData =
    (selectedServiceBarAspects.length &&
      widgetData.filter((item) => selectedServiceBarAspects.includes(item.aspectValue))) ||
    widgetData;

  // replace the client token with the selected client
  const formattedData = replaceClientTokenWith
    ? servicesBarChartDataFormat(filteredData, replaceClientTokenWith)
    : filteredData;

  // sort questions from the highest positive score on top to the lowest positive score on the bottom
  formattedData.sort((scoreA, scoreB) => scoreB.score.positive - scoreA.score.positive);

  // split the data into chunks of size servicesBarChartNumRowsPerTable in each chunk
  const filteredDataChunks = servicesBarChartNumRowsPerTable
    ? splitWidgetDataToChunks(formattedData, servicesBarChartNumRowsPerTable)
    : [formattedData];

  return filteredDataChunks;
};

// Check if there questions with no previous cycle data
export const determineNoDataExplanation = (widgetData: OverallSatisfactionScoreQuestion[]): boolean =>
  widgetData.some((item) => !item.previousCycle);

// Dictionary of the row height according to the number of rows
export const BodyRowHeightCount = {
  [BodyRowHeight.MEDIUM]: 8,
  [BodyRowHeight.LARGE]: 6,
};

export const determineRowHeight = (widgetData: OverallSatisfactionScoreQuestion[]): BodyRowHeight => {
  if (widgetData.length <= BodyRowHeightCount[BodyRowHeight.LARGE]) {
    return BodyRowHeight.LARGE;
  }

  if (
    widgetData.length <= BodyRowHeightCount[BodyRowHeight.MEDIUM] &&
    widgetData.length > BodyRowHeightCount[BodyRowHeight.LARGE]
  ) {
    return BodyRowHeight.MEDIUM;
  }

  return BodyRowHeight.SMALL;
};
